// src/hooks/useHardwareBack.ts
// Capacitor backButton listener with priority chain:
// 1. Drawer / modal handler in stack → close it
// 2. Any page with in-app history → navigate(-1), including primary destinations
// 3. Direct entries use source/parent; only Home without history offers exit-confirm
import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBackHandlerStore } from '@/lib/backHandlers';
import { toast } from '@/lib/toast';
import { usePageBack } from '@/hooks/usePageBack';

const ROOT_PATHS = ['/home'];
const EXIT_WINDOW_MS = 1500;

let exitConfirmedAt = 0;

export function useHardwareBack() {
  const location = useLocation();
  const { t } = useTranslation();
  const goBack = usePageBack();

  useEffect(() => {
    const subPromise = CapacitorApp.addListener('backButton', () => {
      // 1. Drawer / modal handler in stack → close it
      if (useBackHandlerStore.getState().runTop()) return;

      // A primary destination is still a visit in the history stack.
      if ((typeof window.history.state?.idx === 'number' && window.history.state.idx > 0)
        || !ROOT_PATHS.includes(location.pathname)) {
        goBack();
        return;
      }

      // 3. At root → exit-confirm
      const now = Date.now();
      if (now - exitConfirmedAt < EXIT_WINDOW_MS) {
        CapacitorApp.exitApp();
      } else {
        exitConfirmedAt = now;
        toast.info(t('common.pressAgainToExit'));
      }
    });

    return () => {
      subPromise.then((s) => s.remove()).catch(() => {});
    };
  }, [location.pathname, t, goBack]);
}
